import { fetchData, PaginationParameters, PaginationResponse } from "@request";
import { postFiles } from "@request/client";
import { getQueryParams } from "@request/helpers";

type Status =
  | "processing"
  | "approved"
  | "pending_support"
  | "canceled"
  | "created";

export type Appeal = {
  open_order_amount?: number;
  open_order_amount_usdt?: number;
  merchant_amount?: number;
  merchant_amount_usdt?: number;
  card_number: string;
  cardholder_name: string;
  check_image_links: string[] | null;
  created_at: string;
  device_name: string;
  exchange_rate: number;
  id: string;
  message: string;
  operation: "pay_in" | "pay_out";
  order_amount: number;
  order_amount_usdt: number;
  order_invid: string;
  order_uuid: string;
  status: Status;
  updated_at: string;
  platform_approve?: boolean;
  platform_decided_at?: string;
};

export type AppealForm = PaginationParameters<{
  status?: Status;
}>;

export type MerchantAppealForm = PaginationParameters<{
  status?: Status;
  cash_register?: string;
  appeal_type?: string;
}>;

export type CashRegisterSmall = {
  id: string;
  name: string;
};

export type OrderAppeal = {
  appeals: Appeal[];
  order_final_amount: number;
  order_final_amount_usdt: number;
  order_old_status: string;
  order_status: string;
  order_created_at: string;
  order_start_amount: number;
  order_start_amount_usdt: number;
  order_checker_message: string;
  order_invid: string;
  order_type: "pay_in" | "pay_out";
  order_uuid: string;
  cash_register: CashRegisterSmall;
  bank: string;
};

export type AppealResponse = PaginationResponse<{
  order_appeals: OrderAppeal[];
  total_count: number;
}>;

export const getAppeals = async (userId: string, data: AppealForm) => {
  try {
    const queryParams = getQueryParams(data);

    const response = await fetchData<AppealResponse>({
      url: `api/v1/orders/appeals/${userId}?${queryParams}`,
    });

    return response;
  } catch (error) {
    console.error("Appeals failed:", error);
    throw error;
  }
};

export const getMerchantAppealsApi = async (
  userId: string,
  data: MerchantAppealForm
) => {
  try {
    const queryParams = getQueryParams(data);

    const response = await fetchData<AppealResponse>({
      url: `api/v1/merchant/appeals/${userId}?${queryParams}`,
    });

    return response;
  } catch (error) {
    console.error("Appeals failed:", error);
    throw error;
  }
};

export type AppealByOrder = {
  open_order_amount?: number;
  open_order_amount_usdt?: number;
  merchant_amount?: number;
  merchant_amount_usdt?: number;
  card_number: string;
  cardholer_name: string;
  check_image_links: string[] | null;
  exchange_rate: number;
  message: string;
  created_at: string;
  id: string;
  operation: "pay_in" | "pay_out";
  order_amount: number;
  order_amount_usdt: number;
  order_invid: string;
  order_uuid: string;
  status: "approved" | "canceled" | "processing";
  updated_at: string;
};

export type AppealByOrderIdResponse = {
  order_id: string;
  order_invid: string;
  trader_id: string;
  appeals: AppealByOrder[];
};

export const getAppealsByOrderIdApi = async (orderId: string) => {
  try {
    const response = await fetchData<AppealByOrderIdResponse>({
      url: `api/v1/orders/${orderId}/appeals`,
    });

    return response;
  } catch (error) {
    console.error("getAppealsByOrderId failed:", error);
    throw error;
  }
};

export type UploadReceiptRespnose = {
  message: string;
  urls: string[];
};

export const uploadReceiptApi = async (orderId: string, files: FileList) => {
  try {
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("media_files", files[i]);
    }
    const response = await postFiles<UploadReceiptRespnose>({
      method: "POST",
      url: `api/v1/merchants/orders/${orderId}/receipt`,
      data: formData,
    });

    return response;
  } catch (error) {
    console.error("uploadReceiptApi:", error);
    throw error;
  }
};

export type CreateAppealRequest = {
  // invid: string;
  order: string;
  images: string[];
  message: string;
  merchant_amount?: number;
  order_type: "pay_out" | "pay_in";
};

export const createAppealApi = async (data: CreateAppealRequest) => {
  try {
    const response = await fetchData<AppealByOrder>({
      method: "POST",
      url: `api/v1/merchants/orders/appeals`,
      data,
    });

    return response;
  } catch (error) {
    console.error("createAppealApi:", error);
    throw error;
  }
};

export type UpdateAppealApiRequest = {
  actual_received_amount?: number | null;
  approve: boolean;
};

export type UpdateAppealResponse = {
  amount: number;
  error: string;
  processed: boolean;
  status: string;
};

export const updateAppealApi = async (
  appealId: string,
  data: UpdateAppealApiRequest
) => {
  try {
    const response = await fetchData<UpdateAppealResponse>({
      method: "POST",
      url: `api/v1/orders/appeals/${appealId}/process`,
      data,
    });

    return response;
  } catch (error) {
    console.error("updateAppealApi:", error);
    throw error;
  }
};
